import React, { useState } from 'react';
import { Form, Select, DatePicker, InputNumber, Row, Col, Button, Drawer, Table, Input, Spin, Pagination, Space } from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const InvoiceHeader = ({ accounts, documentTypes, showCorredorFields, fetchAccounts, totalItems, currentPage, setCurrentPage, loading, pageSize, filters, setFilters, setSelectedAccount }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  // Función para manejar el cambio de página
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const navigate = useNavigate();

  // Función para aplicar filtros
  const handleFilterChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    setCurrentPage(1); // Reiniciar a la primera página al filtrar
  };

  const padNumber = (value, length) => {
    if (!value) return '';
    return value.toString().padStart(length, '0');
  };


  // Columnas para la tabla de cuentas
  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'cue_nombre',
      key: 'cue_nombre',
      sorter: (a, b) => a.cue_nombre.localeCompare(b.cue_nombre),
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar por nombre"
            value={filters.cue_nombre}
            onChange={e => handleFilterChange('cue_nombre', e.target.value)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => fetchAccounts()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => handleFilterChange('cue_nombre', '')}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    },
    {
      title: 'CUIT',
      dataIndex: 'cue_cuit',
      key: 'cue_cuit',
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar por CUIT"
            value={filters.cue_cuit}
            onChange={e => handleFilterChange('cue_cuit', e.target.value)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => fetchAccounts()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => handleFilterChange('cue_cuit', '')}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    },
    {
      title: 'Tipo',
      dataIndex: 'cue_tipocuenta',
      key: 'cue_tipocuenta',
      render: (tipo) => {
        const tipoMap = {
          'CF': 'Consumidor Final',
          'EX': 'Exento',
          'MON': 'Monotributo',
          'RI': 'Responsable Inscripto'
        };
        return tipoMap[tipo] || tipo;
      }
    }
  ];

  // Función para seleccionar una cuenta de la tabla
  const handleSelectAccount = (record) => {
    console.log('ID CUENTA', record.cue_id);
    form.setFieldsValue({
      account: record.cue_nombre, // Mostrar el nombre en el input visible
      accountId: record.cue_id, // Guardar el ID en un campo oculto
    });
    setSelectedAccount(record.cue_id)
    setDrawerVisible(false);
  };


  return (
    <>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="account" label="Cuenta">
            <Input
              placeholder="Ingresar cuenta"
              value={form.getFieldValue('account') || ''}
              style={{ width: '93%', marginRight: '5px' }}
              disabled
            />
            {/* Campo oculto para asegurar que el valor se guarde en el formulario */}
            <Form.Item name="accountId" hidden>
              <Input />
            </Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => setDrawerVisible(true)}
            />
          </Form.Item>

        </Col>
        <Col span={12}>
          <Form.Item
            name="originDate"
            label="Fecha Origen Comprobante"
            rules={[{ required: true, message: 'Por favor seleccione una fecha' }]}
          >
            <DatePicker
              value={form.getFieldValue('originDate') ? dayjs(form.getFieldValue('originDate')) : null}
              onChange={(value) => {
                let formattedValue = value ? value.format('YYYY-MM-DD') : null;

                if (!value) {
                  form.setFieldsValue({ originDate: null }); // Si no se selecciona, dejar vacío
                } else {
                  form.setFieldsValue({ originDate: formattedValue });
                }
              }}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </Form.Item>

        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Tipo de Comprobante" name="documentType">
            <Select
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
              options={documentTypes.map((tipo) => ({
                value: tipo.tic_id,
                label: tipo.tic_nombre
              }))}
              placeholder="Seleccione un tipo"
            />
          </Form.Item>

        </Col>
        <Col span={6}>
          <Form.Item
            name="pointOfSale"
            label="Punto de Venta"
            rules={[{ required: true, message: 'Por favor ingrese el punto de venta' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={(value) => padNumber(value, 5)} // Formateo para mostrar ceros
              parser={(value) => value.replace(/^0+/, '')} // Remueve los ceros a la izquierda al ingresar
              onChange={(value) => form.setFieldsValue({ pointOfSale: padNumber(value, 5) })} // Almacena el valor con ceros al cambiar
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="documentNumber"
            label="Número"
            rules={[{ required: true, message: 'Por favor ingrese el número' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={(value) => padNumber(value, 8)} // Formateo para mostrar ceros
              parser={(value) => value.replace(/^0+/, '')} // Remueve los ceros a la izquierda al ingresar
              onChange={(value) => form.setFieldsValue({ documentNumber: padNumber(value, 8) })} // Almacena el valor con ceros al cambiar
            />
          </Form.Item>
        </Col>
      </Row>
      {showCorredorFields && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="emisorCorredorCuit"
              label="CUIT Emisor / Corredor (ARCA 033 / 331)"
            >
              <Input
                maxLength={11}
                placeholder="Ej: 30711025118"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="emisorCorredorDenom"
              label="Apellido y Nombre o Denominación (ARCA 033 / 331)"
            >
              <Input
                maxLength={30}
                placeholder="Denominación del emisor/corredor"
              />
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="ivaDate"
            label="Fecha IVA"
          >
            <DatePicker
              value={form.getFieldValue('ivaDate') ? dayjs(form.getFieldValue('ivaDate')) : null}
              onChange={(value) => {
                let formattedValue = value ? value.format('YYYY-MM-DD') : null;

                form.setFieldsValue({
                  ivaDate: formattedValue
                });
              }}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </Form.Item>

        </Col>
      </Row>

      {/* Agregar múltiples fechas de vencimiento */}
      <Form.List name="dueDates">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Row key={key} gutter={16} align="middle">
                <Col span={20}>
                  <Form.Item
                    {...restField}
                    name={[name, 'date']}
                    label={name === 0 ? "Fecha Vencimiento" : ""}
                    rules={[{ required: true, message: 'Ingrese una fecha' }]}
                  >
                    <DatePicker
                      value={
                        form.getFieldValue(['dueDates', name, 'date'])
                          ? dayjs(form.getFieldValue(['dueDates', name, 'date']))
                          : null
                      }
                      onChange={(value) => {
                        let formattedValue = value ? value.format('YYYY-MM-DD') : null;
                        // Get current dueDates or initialize as empty array if undefined
                        const currentDueDates = form.getFieldValue('dueDates') || [];

                        // Update the specific date in the array
                        const updatedDueDates = [...currentDueDates];
                        updatedDueDates[name] = {
                          ...updatedDueDates[name],
                          date: formattedValue
                        };

                        form.setFieldsValue({
                          dueDates: updatedDueDates
                        });
                      }}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                    disabled={fields.length === 1} // No permitir eliminar si solo hay una fecha
                  />
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={() => add()}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Agregar Fecha de Vencimiento
            </Button>
          </>
        )}
      </Form.List>


      {/* Drawer para seleccionar cuentas */}
      <Drawer
        title="Seleccionar Cuenta"
        placement="bottom"
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        height={500}
      >
              <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Input
              placeholder="Buscar por nombre"
              value={filters.cue_nombre}
              onChange={e => handleFilterChange('cue_nombre', e.target.value)}
              style={{ width: '100%' }}
              suffix={<SearchOutlined />}
            />
          </Col>
          <Col span={12}>
            <Input
              placeholder="Buscar por CUIT"
              value={filters.cue_cuit}
              onChange={e => handleFilterChange('cue_cuit', e.target.value)}
              style={{ width: '100%' }}
              suffix={<SearchOutlined />}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Col>
            <Button type="primary" onClick={fetchAccounts} style={{ marginRight: 8 }}>
              Buscar
            </Button>
            <Button onClick={() => setFilters({ cue_nombre: '', cue_cuit: '' })}>
              Limpiar Filtros
            </Button>
          </Col>
          <Col>
            <Button type="primary" onClick={() => navigate('/cuenta-administrador')}>
              Cuentas
            </Button>
          </Col>
        </Row>
      </div>

        {loading ? (
          <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />
        ) : (
          <>
            <Table
              dataSource={accounts}
              columns={columns}
              rowKey="cue_id"
              pagination={false}
              onRow={(record) => ({
                onClick: () => handleSelectAccount(record),
                style: { cursor: 'pointer' }
              })}
              scroll={{ y: 240 }}
            />
            <Pagination
              current={currentPage}
              total={totalItems}
              pageSize={pageSize}
              onChange={handlePageChange}
              style={{ marginTop: 16, textAlign: 'center' }}
            />
          </>
        )}
      </Drawer>
    </>
  );
};

export default InvoiceHeader;